import { useEffect } from 'react';

import {
  invalidateDxfSceneStatusQuery,
  PartWorkerTasks,
  TaskStatus,
  useGetDxfSceneStatus,
} from '@frontend/api';

import { useTaskStatusTracking } from 'utils/hooks/taskTracking/useTaskStatusTracking';

/**
 * Track a part file's scene cache, refetching when a build announces it finished.
 *
 * The initial fetch does double duty: it reports the current state *and* is what
 * enqueues a build for a file whose scene is missing or stale. After that, this
 * hook waits for a 'task_status' message from the build.
 *
 * @param partFileUuid uuid of the part file to track; `null` disables the hook.
 * @param enabled whether to fetch and watch at all — pass `false` when no scene
 *   is expected, so an idle viewer neither requests nor triggers a build.
 */
export const useLiveDxfScene = ({
  partFileUuid,
  enabled = true,
}: {
  partFileUuid: string | null;
  enabled?: boolean;
}) => {
  const query = useGetDxfSceneStatus(partFileUuid, {
    queryOptions: { enabled },
  });

  const { registerCallback, unregisterCallback } = useTaskStatusTracking();

  useEffect(() => {
    if (!partFileUuid || !enabled) {
      return;
    }
    const callbackKey = `dxf_scene_${partFileUuid}`;

    registerCallback(callbackKey, ({ updateMessages }) => {
      for (const update of updateMessages) {
        if (
          update.task === PartWorkerTasks.DXF_SCENE &&
          update.entityKey === partFileUuid &&
          update.status === TaskStatus.COMPLETED
        ) {
          invalidateDxfSceneStatusQuery(partFileUuid);
          break;
        }
      }
    });

    return () => unregisterCallback(callbackKey);
  }, [partFileUuid, enabled, registerCallback, unregisterCallback]);

  return query;
};
